import React, { useEffect, useRef, useState } from "react";

interface Props {
  value: string;
  currency: string;
  onValueChange: (val: string) => void;
  onCurrencyChange: (val: "INR" | "USD") => void;
  disabled?: boolean;
  currencyDisabled?: boolean;
}

const CurrencyInputCompact: React.FC<Props> = ({
  value,
  currency,
  onValueChange,
  onCurrencyChange,
  disabled = false,
  currencyDisabled = false,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const openDropdown = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      background: "#fff",
      border: "1px solid #ccc",
      borderRadius: "4px",
    });

    setOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        height: "40px",
        width: "100%",
        background: disabled ? "#e9ecef" : "#fff",
      }}
    >
      {/* Input */}
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const val = e.target.value;
          if (/^\d*\.?\d*$/.test(val)) {
            onValueChange(val);
          }
        }}
        style={{
          flex: 3,
          border: "none",
          outline: "none",
          padding: "0 8px",
          height: "100%",
        }}
      />

      {/* Divider */}
      <div style={{ width: "1px", background: "#ced4da", height: "100%" }} />

      {/* Currency */}
      <div
        ref={triggerRef}
        style={{
          flex: 1,
          textAlign: "center",
          cursor: currencyDisabled ? "not-allowed" : "pointer",
          position: "relative",
        }}
        onClick={() => !currencyDisabled && openDropdown()}
      >
        {currency}

        {open && (
          <ul
            ref={dropdownRef}
            className="list-group"
            style={{
              ...dropdownStyle,
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {["INR", "USD"].map((cur) => {
              const isSelected = cur === currency;

              return (
                <li
                  key={cur}
                  className="list-group-item"
                  style={{
                    cursor: "pointer",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 10px",
                    backgroundColor: isSelected ? "#e9f3ff" : "#fff",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onCurrencyChange(cur as "INR" | "USD");
                    setOpen(false);
                  }}
                >
                  <input type="radio" checked={isSelected} readOnly />
                  <span>{cur}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CurrencyInputCompact;
